import React, { useMemo, useState } from "react";
import "./EosApp.scss";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AppsHeader from "../components/AppsHeader";
import { computeEOS } from "./EosCalculator";
import { Instructions } from "./components/Instructions";
import { InfoModal } from "./components/InfoModal";
import { EosForm } from "./components/EosForm";
import type { TForm, TErrors, AntibioticTreatment } from "./components/EosForm";
import { EosResultsAccordion } from "./components/EosResultsAccordion";
import { resolveClinicalCondition } from "./EosManager/resolve";
import type { ClinicalCondition } from "./EosManager/recommendations";

import { Card, CardBody } from "@heroui/card";
import { Form } from "@heroui/react";

import { HiArrowPath } from "react-icons/hi2";

const intercept = 0.5; // constant for Israel

const isAssuta = window.location.pathname.startsWith("/assuta");
// todo fix
const MIN_TEMPRATURE_BEFORE_FORCING_ANTIBIOTICS = isAssuta ? 39 : 41;

// Antibiotic options (shared semantics with form)
const ANTIBIOTIC_OPTIONS: Array<{
  key: AntibioticTreatment;
  label: string;
}> = [
  { key: "none", label: "ללא או טיפול פחות משעתיים לפני הלידה" },
  { key: "GBS-2", label: "ספציפית ל-GBS מעל שעתיים לפני הלידה" },
  { key: "broad-2", label: "רחבת טווח 2 - 3.9 שעות לפני הלידה" },
  { key: "broad-4", label: "רחבת טווח מעל 4 שעות לפני הלידה" },
];

function EosApp() {
  const initialFormState: TForm = {};

  const [form, setForm] = useState<TForm>(initialFormState);
  const [errors, setErrors] = useState<TErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [showSummary, setShowSummary] = useState(true);

  const [infoModalId, setInfoModalId] = useState<1 | 2 | 3 | 4 | "" | null>(null);
  const closeInfoModal = () => setInfoModalId("");

  const handleReset = () => {
    setForm(initialFormState);
    setErrors({});
    setSubmitted(false);
    setShowSummary(true);
  };

  function handleChange({ value, name }: { value: any; name: keyof TForm }) {
    setForm((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  }

  const formHasError = !!(
    errors.temperature ||
    errors.pregnancyLengthDays ||
    errors.pregnancyLengthWeeks
  );

  const formKeys: Array<keyof TForm> = [
    "antibioticTreatment",
    "gbs",
    "pregnancyLengthDays",
    "pregnancyLengthWeeks",
    "rom",
    "temperature",
  ];

  const formNotFilled = formKeys.some((key) =>
    key === "pregnancyLengthDays"
      ? false
      : !Boolean(form[key] || form[key] === 0)
  );

  const isInputValid = () => !formHasError && !formNotFilled;

  // show results only after user clicks "Continue"
  const hideResult = !submitted || formHasError || formNotFilled;

  const { eos, eosString, eosPerClinicalCondition } = useMemo(() => {
    if (hideResult) {
      return {
        eos: 0,
        eosString: "",
        eosPerClinicalCondition: {} as Record<string, string | number>,
      };
    }
    return computeEOS(intercept, form as any);
  }, [hideResult, form]);

  const resolved = useMemo(() => {
    if (hideResult) return null;

    const make = (clinicalCondition: ClinicalCondition) =>
      resolveClinicalCondition({
        clinicalCondition,
        eosPerClinicalCondition,
        eos,
        temperature: form.temperature,
        minTemperatureBeforeForcingAntibiotics:
          MIN_TEMPRATURE_BEFORE_FORCING_ANTIBIOTICS,
      });

    return {
      well: make("Well Appearing"),
      equivocal: make("Equivocal"),
      illness: make("Clinical Illness"),
    };
  }, [hideResult, eosPerClinicalCondition, eos, form.temperature]);

  const [page, setPage] = useState<"CALCULATOR" | "INSTRUCTIONS">(
    "CALCULATOR"
  );

  // ---------- formatting helpers ----------
  const formatValue = (v: unknown) => {
    if (v === null || v === undefined || v === "") return "___";
    return String(v);
  };

  const formatPregnancyLength = () => {
    const w = (form as any).pregnancyLengthWeeks;
    const d = (form as any).pregnancyLengthDays;

    if (w === null || w === undefined) return "___";

    const days = d === null || d === undefined ? 0 : d;
    return `${w}+${days}`;
  };

  const formatGbs = () => {
    const v = (form as any).gbs;
    if (!v) return "___";

    const map: Record<string, string> = {
      positive: "חיובי",
      negative: "שלילי",
      unknown: "לא ידוע",
    };

    return map[String(v)] ?? String(v);
  };

  const formatAntibiotics = () => {
    const value = form.antibioticTreatment as
      | AntibioticTreatment
      | undefined;

    if (!value) return "___";

    const option = ANTIBIOTIC_OPTIONS.find((o) => o.key === value);
    return option?.label ?? "___";
  };
  // --------------------------------------

  return (
    <div className="eos">
      {page === "CALCULATOR" && (
        <div className="EosApp eos-container main-content corrected-age-container">
          <AppsHeader title="אלח דם" />

          {submitted && (
            <div className="eos-actions">
              <button
                type="button"
                className="eos-actions__secondary"
                onClick={() => {
                  handleReset();
                  setPage("CALCULATOR");
                }}
              >
               <HiArrowPath />
                התחל מחדש
              </button>

              <button
                type="button"
                className="eos-actions__primary"
                onClick={() => {
                  setPage("INSTRUCTIONS");
                }}
              >
                דגשים EOS
              </button>
            </div>
          )}

          <Card
            radius="sm"
            className="w-full"
            style={{
              borderRadius: "var(--radius-m, 8px)",
              background: "var(--neutral-light, #EEFFFD)",
              boxShadow:
                "0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)",
            }}
          >
            <CardBody className="space-y-4">
              <div
                className="eos-content-wrapper"
                style={{ width: "100%", maxWidth: "380px", marginInline: "auto" }}
              >
                {!submitted ? (
                  <Form
                    validationBehavior="aria"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <EosForm
                      form={form}
                      errors={errors}
                      setErrors={setErrors}
                      onOpenInfoModal={(id) =>
                          setInfoModalId(id as 1 | 2 | 3 | 4 | "" | null)
                        }
                      onChange={handleChange}
                      onClear={handleReset}
                      onContinue={() => setSubmitted(true)}
                      continueDisabled={!isInputValid()}
                    />
                  </Form>
                ) : (
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "6px 12px 6px",
                        color: "#0C3058",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: 400,
                          marginBottom: "6px",
                        }}
                      >
                        סיכון לאלח דם מוקדם
                      </div>

                      <div
                        style={{
                          fontSize: "1.6rem",
                          fontWeight: 500,
                        }}
                      >
                        <span data-testid="eosStatistics">
                          {eosString || "___"}
                        </span>{" "}
                        לאלף לידות
                      </div>
                    </div>

                    {showSummary && (
                      <div
                        data-testid="eos-summary"
                        style={{
                          textAlign: "right",
                          padding: "10px 12px 4px",
                          color: "#0C3058",
                          lineHeight: 1.8,
                          fontSize: "1.05rem",
                          fontWeight: 700,
                        }}
                      >
                        <div>
                          חום אימהי מקסימלי:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {formatValue(form.temperature)}
                          </span>
                        </div>
                        <div>
                          משך היריון:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {formatPregnancyLength()}
                          </span>
                        </div>
                        <div>
                          משך פקיעת הקרומים:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {formatValue(form.rom)} שעות
                          </span>
                        </div>
                        <div>
                          סטטוס GBS:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {formatGbs()}
                          </span>
                        </div>
                        <div style={{ fontWeight: 700 }}>
                          טיפול אנטיביוטי בלידה:
                        </div>
                        <div>
                          <span style={{ fontWeight: 400 }}>
                            {formatAntibiotics()}
                          </span>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowSummary((v) => !v)}
                      style={{
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        color: "#0C3058",
                        fontWeight: 800,
                        padding: "10px 0 6px",
                        cursor: "pointer",
                      }}
                    >
                      {showSummary ? "הצג פחות" : "הצג עוד"}
                    </button>

                    <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    aria-label="back to edit"
                    style={{
                      position: "absolute",
                      left: -8,
                      bottom: -6,
                      background: "transparent",
                      border: "none",
                      color: "#0C3058",
                      width: 56,
                      height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    >
                    <FontAwesomeIcon icon={faPen} />
                    </button>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {!hideResult && resolved && (
            <EosResultsAccordion
              eosPerClinicalCondition={eosPerClinicalCondition}
              resolved={resolved}
              onOpenInfoModal={(id) => setInfoModalId(id)}
              onReset={handleReset}
            />
          )}

          <InfoModal id={infoModalId} onClose={closeInfoModal} />
        </div>
      )}

      {page === "INSTRUCTIONS" && (
        <div className="EosApp eos-container main-content corrected-age-container">
          <Instructions setPage={setPage} />
        </div>
      )}
    </div>
  );
}

export default EosApp;